const SUMMARY_FIELD_ALIASES = {
  name: [
    "name",
    "studentname",
    "student_name",
    "fullname",
    "full_name",
    "studentfullname",
    "student_full_name",
  ],
  photoUrl: [
    "studentimageurl",
    "studentimage",
    "studentphoto",
    "studentphotourl",
    "profileimage",
    "profileimageurl",
    "profilephoto",
    "profilephotourl",
    "photo",
    "photourl",
    "image",
    "imageurl",
    "avatar",
    "avatarurl",
  ],
  className: [
    "class",
    "classname",
    "class_name",
    "grade",
    "standard",
    "course",
  ],
  section: ["section", "sectionname", "section_name", "division"],
  studentId: ["uid"],
  activeQr: ["activeqr", "active_qr"],
};

const LABEL_OVERRIDES = {
  uid: "Student ID",
  id: "ID",
  classname: "Class",
  sectionname: "Section",
  studentimageurl: "Student Photo",
  profileimageurl: "Profile Image URL",
  photourl: "Photo URL",
  qrimageurl: "QR Image URL",
  activeqr: "Active QR",
  dob: "DOB",
  aadhaar: "Aadhaar",
  api: "API",
  upi: "UPI",
  url: "URL",
};

const PERSONAL_PATTERNS = [
  "dob",
  "birth",
  "gender",
  "blood",
  "religion",
  "category",
  "nationality",
  "aadhaar",
  "age",
  "identity",
  "caste",
  "mothertongue",
  "language",
  "personal",
];

const ACADEMIC_PATTERNS = [
  "class",
  "section",
  "roll",
  "admission",
  "academic",
  "grade",
  "result",
  "subject",
  "marks",
  "attendance",
  "exam",
  "session",
  "batch",
  "house",
  "stream",
  "course",
  "teacher",
  "scholar",
  "enrollment",
  "registration",
];

const CONTACT_PATTERNS = [
  "father",
  "mother",
  "parent",
  "guardian",
  "phone",
  "mobile",
  "email",
  "mail",
  "address",
  "contact",
  "whatsapp",
  "emergency",
  "city",
  "state",
  "country",
  "pincode",
  "postal",
];

const HIDDEN_FIELD_KEYS = new Set([
  "fathername",
  "father_name",
  "mothername",
  "mother_name",
]);

function normalizeKey(key = "") {
  return String(key)
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();
}

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === "[object Object]";
}

function isTimestampLike(value) {
  return (
    value &&
    typeof value === "object" &&
    (typeof value.toDate === "function" ||
      (typeof value.seconds === "number" &&
        typeof value.nanoseconds === "number"))
  );
}

function isDateLike(value) {
  return value instanceof Date || isTimestampLike(value);
}

function toDateValue(value) {
  if (value instanceof Date) {
    return value;
  }

  if (isTimestampLike(value) && typeof value.toDate === "function") {
    return value.toDate();
  }

  if (isTimestampLike(value)) {
    return new Date(value.seconds * 1000);
  }

  return null;
}

function formatDateValue(value) {
  const dateValue = toDateValue(value);

  if (!dateValue || Number.isNaN(dateValue.getTime())) {
    return "Not Available";
  }

  const hasTime =
    dateValue.getHours() !== 0 ||
    dateValue.getMinutes() !== 0 ||
    dateValue.getSeconds() !== 0;

  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    ...(hasTime ? { timeStyle: "short" } : {}),
  }).format(dateValue);
}

export function hasDisplayValue(value) {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === "boolean") {
    return true;
  }

  if (typeof value === "number") {
    return !Number.isNaN(value);
  }

  if (typeof value === "string") {
    return value.trim() !== "";
  }

  if (isDateLike(value)) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.some((item) => hasDisplayValue(item));
  }

  if (isPlainObject(value)) {
    return Object.values(value).some((item) => hasDisplayValue(item));
  }

  return Boolean(value);
}

export function isRenderablePrimitive(value) {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    isDateLike(value)
  );
}

export function isUrl(value = "") {
  return /^https?:\/\//i.test(String(value).trim());
}

export function formatDisplayValue(value) {
  if (!hasDisplayValue(value)) {
    return "Not Available";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "number") {
    return String(value);
  }

  if (isDateLike(value)) {
    return formatDateValue(value);
  }

  return String(value).trim();
}

export function formatFieldLabel(key = "") {
  const normalizedKey = normalizeKey(key);

  if (LABEL_OVERRIDES[normalizedKey]) {
    return LABEL_OVERRIDES[normalizedKey];
  }

  const readable = String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!readable) {
    return "Details";
  }

  return readable
    .split(" ")
    .map((word) => {
      const normalizedWord = normalizeKey(word);

      if (LABEL_OVERRIDES[normalizedWord]) {
        return LABEL_OVERRIDES[normalizedWord];
      }

      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function findTopLevelField(source, aliases = []) {
  const aliasSet = new Set(aliases.map((item) => normalizeKey(item)));

  for (const [key, value] of Object.entries(source || {})) {
    if (!hasDisplayValue(value)) {
      continue;
    }

    if (aliasSet.has(normalizeKey(key))) {
      return {
        key,
        path: [key],
        value,
        isTopLevel: true,
      };
    }
  }

  return null;
}

function findNestedField(source, aliases = [], path = []) {
  const aliasSet = new Set(aliases.map((item) => normalizeKey(item)));

  if (Array.isArray(source)) {
    for (let index = 0; index < source.length; index += 1) {
      const value = source[index];

      if (isPlainObject(value) || Array.isArray(value)) {
        const result = findNestedField(value, aliases, [...path, index]);

        if (result) {
          return result;
        }
      }
    }

    return null;
  }

  if (!isPlainObject(source)) {
    return null;
  }

  for (const [key, value] of Object.entries(source)) {
    if (!hasDisplayValue(value)) {
      continue;
    }

    if (aliasSet.has(normalizeKey(key))) {
      return {
        key,
        path: [...path, key],
        value,
        isTopLevel: path.length === 0,
      };
    }
  }

  for (const [key, value] of Object.entries(source)) {
    if (isPlainObject(value) || Array.isArray(value)) {
      const result = findNestedField(value, aliases, [...path, key]);

      if (result) {
        return result;
      }
    }
  }

  return null;
}

function resolveSummaryField(source, aliases) {
  return findTopLevelField(source, aliases) || findNestedField(source, aliases);
}

export function extractStudentSummary(student = {}, fallbackId = "") {
  const omittedKeys = new Set();
  const nameField = resolveSummaryField(student, SUMMARY_FIELD_ALIASES.name);
  const photoField = resolveSummaryField(
    student,
    SUMMARY_FIELD_ALIASES.photoUrl,
  );
  const classField = resolveSummaryField(
    student,
    SUMMARY_FIELD_ALIASES.className,
  );
  const sectionField = resolveSummaryField(
    student,
    SUMMARY_FIELD_ALIASES.section,
  );
  const idField = resolveSummaryField(student, SUMMARY_FIELD_ALIASES.studentId);
  const activeQrField = resolveSummaryField(
    student,
    SUMMARY_FIELD_ALIASES.activeQr,
  );

  [nameField, photoField, classField, sectionField, idField, activeQrField]
    .filter(Boolean)
    .forEach((field) => {
      if (field.isTopLevel) {
        omittedKeys.add(field.key);
      }
    });

  return {
    name: nameField ? formatDisplayValue(nameField.value) : "Student Profile",
    photoUrl: photoField?.value || "",
    className: classField ? formatDisplayValue(classField.value) : "",
    section: sectionField ? formatDisplayValue(sectionField.value) : "",
    studentId: formatDisplayValue(
      idField?.value || student.uid || fallbackId || "",
    ),
    activeQr: Boolean(activeQrField?.value),
    omittedKeys,
  };
}

function matchesPattern(patterns, normalizedKey) {
  return patterns.some((pattern) => normalizedKey.includes(pattern));
}

export function classifyStudentField(key = "") {
  const normalizedKey = normalizeKey(key);

  if (matchesPattern(ACADEMIC_PATTERNS, normalizedKey)) {
    return "academic";
  }

  if (matchesPattern(CONTACT_PATTERNS, normalizedKey)) {
    return "contact";
  }

  if (matchesPattern(PERSONAL_PATTERNS, normalizedKey)) {
    return "personal";
  }

  return "other";
}

export function buildStudentSections(student = {}, omittedKeys = new Set()) {
  const groupedSections = {
    personal: [],
    academic: [],
    contact: [],
    other: [],
  };

  Object.entries(student).forEach(([key, value]) => {
    if (
      omittedKeys.has(key) ||
      HIDDEN_FIELD_KEYS.has(normalizeKey(key)) ||
      !hasDisplayValue(value)
    ) {
      return;
    }

    const sectionKey = classifyStudentField(key);

    groupedSections[sectionKey].push({
      key,
      label: formatFieldLabel(key),
      value,
    });
  });

  return [
    {
      key: "personal",
      title: "Personal Details",
      fields: groupedSections.personal,
    },
    {
      key: "academic",
      title: "Academic Info",
      fields: groupedSections.academic,
    },
    { key: "contact", title: "Contact Info", fields: groupedSections.contact },
    { key: "other", title: "Other Details", fields: groupedSections.other },
  ].filter((section) => section.fields.length > 0);
}

export function isPlainValueObject(value) {
  return isPlainObject(value);
}
